<div class="chat-shell" (dragover)="onDragOver($event)" (drop)="onDrop($event)">
  <aside class="chat-sidebar">
    <header class="sidebar-head">
      <div>
        <h1>Messages</h1>
        <p>Internal collaboration</p>
      </div>
      <div class="head-actions">
        <button mat-icon-button type="button" matTooltip="New direct message" (click)="openNewDm()">
          <mat-icon>person_add</mat-icon>
        </button>
        <button mat-icon-button type="button" matTooltip="New group" (click)="openNewGroup()">
          <mat-icon>group_add</mat-icon>
        </button>
      </div>
    </header>

    <div class="sidebar-search">
      <mat-icon>search</mat-icon>
      <input
        type="search"
        placeholder="Search chats & messages"
        [ngModel]="sidebarSearch()"
        (ngModelChange)="onSidebarSearch($event)" />
    </div>

    @if (searchHits().length) {
      <div class="search-hits">
        @for (hit of searchHits(); track hit.messageId) {
          <button type="button" class="hit" (click)="openSearchHit(hit)">
            <strong>{{ hit.roomName }}</strong>
            <span>{{ hit.snippet }}</span>
            <small>{{ hit.senderName }} · {{ formatTime(hit.createdAt) }}</small>
          </button>
        }
      </div>
    }

    <div class="tabs">
      <button type="button" [class.active]="sidebarTab() === 'chats'" (click)="sidebarTab.set('chats')">Chats</button>
      <button type="button" [class.active]="sidebarTab() === 'groups'" (click)="sidebarTab.set('groups')">Groups</button>
    </div>

    @if (loading()) {
      <div class="center"><mat-spinner diameter="28" /></div>
    } @else if (error() && !conversations().length) {
      <p class="error">{{ error() }}</p>
    } @else {
      @if (pinnedConversations().length) {
        <div class="section-label">Pinned</div>
        @for (c of pinnedConversations(); track c.id) {
          <button type="button" class="conv" [class.active]="selected()?.id === c.id" (click)="openConversation(c)">
            <span class="avatar" [style.background-image]="c.avatarUrl ? 'url(' + fileUrl(c.avatarUrl) + ')' : null">
              @if (!c.avatarUrl) { {{ c.name.charAt(0) }} }
              @if (c.roomType === 0) {
                <i class="presence" [class]="presenceClass(c.peerPresence)"></i>
              }
            </span>
            <span class="meta">
              <span class="row">
                <strong>{{ c.name }}</strong>
                <time>{{ formatTime(c.lastMessageAt) }}</time>
              </span>
              <span class="preview">{{ c.lastMessagePreview || 'No messages yet' }}</span>
            </span>
            @if (c.unreadCount > 0) {
              <span class="badge">{{ c.unreadCount > 99 ? '99+' : c.unreadCount }}</span>
            }
          </button>
        }
      }

      <div class="section-label">Recent</div>
      @for (c of recentConversations(); track c.id) {
        <button type="button" class="conv" [class.active]="selected()?.id === c.id" (click)="openConversation(c)">
          <span class="avatar" [style.background-image]="c.avatarUrl ? 'url(' + fileUrl(c.avatarUrl) + ')' : null">
            @if (!c.avatarUrl) { {{ c.name.charAt(0) }} }
            @if (c.roomType === 0) {
              <i class="presence" [class]="presenceClass(c.peerPresence)"></i>
            }
          </span>
          <span class="meta">
            <span class="row">
              <strong>{{ c.name }}</strong>
              <time>{{ formatTime(c.lastMessageAt) }}</time>
            </span>
            <span class="preview">
              @if (c.isMuted) { <mat-icon class="tiny">notifications_off</mat-icon> }
              {{ c.lastMessagePreview || 'No messages yet' }}
            </span>
          </span>
          @if (c.unreadCount > 0) {
            <span class="badge">{{ c.unreadCount > 99 ? '99+' : c.unreadCount }}</span>
          }
        </button>
      } @empty {
        <p class="empty-side">No conversations yet. Start a direct message or create a group.</p>
      }
    }
  </aside>

  <section class="chat-main">
    @if (!selected()) {
      <div class="empty-main">
        <mat-icon>forum</mat-icon>
        <h2>Select a conversation</h2>
        <p>Message colleagues, share files, and collaborate on projects.</p>
      </div>
    } @else {
      <header class="thread-head">
        <div class="who">
          <span class="avatar lg" [style.background-image]="selected()!.avatarUrl ? 'url(' + fileUrl(selected()!.avatarUrl) + ')' : null">
            @if (!selected()!.avatarUrl) { {{ selected()!.name.charAt(0) }} }
            @if (selected()!.roomType === 0) {
              <i class="presence" [class]="presenceClass(selected()!.peerPresence)"></i>
            }
          </span>
          <div>
            <h2>{{ selected()!.name }}</h2>
            <p>
              @if (selected()!.roomType === 0) {
                {{ selected()!.peerPresence === 1 ? 'Online' : (selected()!.peerLastSeenAt ? ('Last seen ' + formatTime(selected()!.peerLastSeenAt)) : 'Offline') }}
              } @else {
                {{ selected()!.memberCount }} members · {{ selected()!.roomTypeLabel }}
              }
            </p>
          </div>
        </div>
        <div class="actions">
          <button mat-icon-button type="button" [matTooltip]="selected()!.isPinned ? 'Unpin' : 'Pin'" (click)="togglePinConversation()">
            <mat-icon>{{ selected()!.isPinned ? 'push_pin' : 'push_pin' }}</mat-icon>
          </button>
          <button mat-icon-button type="button" [matTooltip]="selected()!.isMuted ? 'Unmute' : 'Mute'" (click)="toggleMute()">
            <mat-icon>{{ selected()!.isMuted ? 'notifications_off' : 'notifications' }}</mat-icon>
          </button>
          <button mat-icon-button type="button" matTooltip="Details" (click)="rightOpen.set(!rightOpen())">
            <mat-icon>info</mat-icon>
          </button>
          <button mat-icon-button type="button" [matMenuTriggerFor]="roomMenu">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #roomMenu="matMenu">
            <button mat-menu-item type="button" (click)="leaveOrArchive()">
              {{ selected()!.roomType === 0 ? 'Archive chat' : 'Leave group' }}
            </button>
          </mat-menu>
        </div>
      </header>

      <div class="thread" #threadScroll (scroll)="onThreadScroll()">
        @if (loadingMessages()) {
          <div class="center"><mat-spinner diameter="28" /></div>
        }
        @for (m of messages(); track m.id; let i = $index) {
          @if (showDaySeparator(i)) {
            <div class="day">{{ dayLabel(m.createdAt) }}</div>
          }
          <article class="bubble" [class.mine]="isMine(m)" [class.deleted]="m.deletedForEveryone">
            @if (!isMine(m)) {
              <span class="avatar sm" [style.background-image]="m.senderAvatarUrl ? 'url(' + fileUrl(m.senderAvatarUrl) + ')' : null">
                @if (!m.senderAvatarUrl) { {{ m.senderName.charAt(0) }} }
              </span>
            }
            <div class="body">
              @if (!isMine(m)) {
                <div class="sender">{{ m.senderName }}</div>
              }
              @if (m.replyPreview) {
                <div class="reply-ref">{{ m.replyPreview }}</div>
              }
              @if (m.forwardedFromMessageId) {
                <div class="forwarded">Forwarded</div>
              }
              @if (editingId() === m.id) {
                <div class="edit-box">
                  <textarea [ngModel]="editDraft()" (ngModelChange)="editDraft.set($event)" rows="2"></textarea>
                  <button mat-button type="button" color="primary" (click)="saveEdit(m)">Save</button>
                  <button mat-button type="button" (click)="editingId.set(null)">Cancel</button>
                </div>
              } @else if (m.deletedForEveryone) {
                <p class="deleted-text">This message was deleted.</p>
              } @else {
                <p class="text" [innerText]="m.content"></p>
                @if (taskLink(m.content); as link) {
                  <a class="task-link" [routerLink]="link">Open linked work item</a>
                }
              }
              @if (m.attachments.length) {
                <div class="atts">
                  @for (a of m.attachments; track a.id) {
                    @if (a.isImage) {
                      <button type="button" class="img" (click)="previewImage.set(fileUrl(a.url || a.thumbnailUrl))">
                        <img [src]="fileUrl(a.thumbnailUrl || a.url)" [alt]="a.originalFileName" />
                      </button>
                    } @else {
                      <a class="file" [href]="fileUrl(a.url)" target="_blank" rel="noopener">
                        <mat-icon>attach_file</mat-icon>
                        <span>{{ a.originalFileName }}</span>
                        <small>{{ formatBytes(a.sizeBytes) }}</small>
                      </a>
                    }
                  }
                </div>
              }
              @if (m.reactions.length) {
                <div class="reactions">
                  @for (r of m.reactions; track r.emoji) {
                    <button type="button" [class.mine-react]="r.reactedByMe" (click)="react(m, r.emoji)">
                      {{ r.emoji }} {{ r.count }}
                    </button>
                  }
                </div>
              }
              <div class="foot">
                <time>{{ formatTime(m.createdAt) }}</time>
                @if (m.isEdited) { <span>edited</span> }
                @if (m.isPinned) { <mat-icon class="tiny">push_pin</mat-icon> }
                <button mat-icon-button type="button" class="more" [matMenuTriggerFor]="msgMenu">
                  <mat-icon>more_horiz</mat-icon>
                </button>
                <mat-menu #msgMenu="matMenu">
                  <button mat-menu-item type="button" (click)="setReply(m)">Reply</button>
                  <button mat-menu-item type="button" (click)="copyMessage(m)">Copy</button>
                  <button mat-menu-item type="button" (click)="react(m, '👍')">👍 React</button>
                  <button mat-menu-item type="button" (click)="pinMessage(m, !m.isPinned)">{{ m.isPinned ? 'Unpin' : 'Pin' }}</button>
                  @if (isMine(m) && !m.deletedForEveryone) {
                    <button mat-menu-item type="button" (click)="startEdit(m)">Edit</button>
                    <button mat-menu-item type="button" (click)="deleteForEveryone(m)">Delete for everyone</button>
                  }
                  <button mat-menu-item type="button" (click)="deleteForMe(m)">Delete for me</button>
                  @for (c of conversations(); track c.id) {
                    @if (c.id !== selected()?.id) {
                      <button mat-menu-item type="button" (click)="forwardTo(m, c)">Forward to {{ c.name }}</button>
                    }
                  }
                </mat-menu>
              </div>
            </div>
          </article>
        }
      </div>

      @if (typingLabel()) {
        <div class="typing">{{ typingLabel() }}</div>
      }

      <footer class="composer">
        @if (replyTo(); as r) {
          <div class="reply-bar">
            <span>Replying to {{ r.senderName }}: {{ r.content | slice:0:80 }}</span>
            <button mat-icon-button type="button" (click)="clearReply()"><mat-icon>close</mat-icon></button>
          </div>
        }
        @if (pendingAttachments().length) {
          <div class="pending">
            @for (a of pendingAttachments(); track $index; let i = $index) {
              <span class="chip">
                {{ a.originalFileName }}
                <button type="button" (click)="removePending(i)"><mat-icon>close</mat-icon></button>
              </span>
            }
          </div>
        }
        <div class="composer-row">
          <input #fileInput type="file" multiple hidden (change)="onFilesSelected($event)"
            accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,image/*" />
          <button mat-icon-button type="button" matTooltip="Attach" (click)="fileInput.click()">
            <mat-icon>attach_file</mat-icon>
          </button>
          <textarea
            rows="1"
            placeholder="Write a message…"
            [ngModel]="composer()"
            (ngModelChange)="onComposerInput($event)"
            (keydown.enter)="$event.preventDefault(); send()"></textarea>
          <button mat-flat-button color="primary" type="button" (click)="send()" [disabled]="uploadProgress()">
            @if (uploadProgress()) { <mat-spinner diameter="18" /> } @else { Send }
          </button>
        </div>
      </footer>
    }
  </section>

  @if (selected() && rightOpen()) {
    <aside class="chat-right">
      <h3>Details</h3>

      @if (selected()!.roomType !== 0) {
        <section>
          <h4>Members</h4>
          @for (m of members(); track m.userId) {
            <button type="button" class="member" (click)="openEmployeeProfile(m)">
              <span class="avatar sm" [style.background-image]="m.avatarUrl ? 'url(' + fileUrl(m.avatarUrl) + ')' : null">
                @if (!m.avatarUrl) { {{ m.displayName.charAt(0) }} }
                <i class="presence" [class]="presenceClass(m.presence)"></i>
              </span>
              <span>
                <strong>{{ m.displayName }}</strong>
                <small>@if (m.isOwner) { Owner } @else if (m.isAdmin) { Admin }</small>
              </span>
            </button>
          }
        </section>
      }

      <section>
        <h4>Pinned</h4>
        @for (p of pinned(); track p.id) {
          <p class="pin-item">{{ p.content || '(attachment)' }}</p>
        } @empty {
          <p class="muted">No pinned messages</p>
        }
      </section>

      <section>
        <h4>Images</h4>
        <div class="gallery">
          @for (img of sharedImages(); track img.id) {
            <button type="button" (click)="previewImage.set(fileUrl(img.url || img.thumbnailUrl))">
              <img [src]="fileUrl(img.thumbnailUrl || img.url)" [alt]="img.originalFileName" />
            </button>
          } @empty {
            <p class="muted">No images yet</p>
          }
        </div>
      </section>

      <section>
        <h4>Files</h4>
        @for (f of sharedFiles(); track f.id) {
          <a class="file-row" [href]="fileUrl(f.url)" target="_blank" rel="noopener">
            <mat-icon>description</mat-icon>
            <span>{{ f.originalFileName }}</span>
            <small>{{ formatBytes(f.sizeBytes) }}</small>
          </a>
        } @empty {
          <p class="muted">No files yet</p>
        }
      </section>
    </aside>
  }
</div>

@if (showNewDm()) {
  <div class="modal-backdrop" (click)="showNewDm.set(false)">
    <div class="modal" (click)="$event.stopPropagation()">
      <h3>New message</h3>
      <input type="search" placeholder="Search people" [ngModel]="dirSearch()" (ngModelChange)="onDirSearch($event)" />
      <div class="dir-list">
        @for (u of directory(); track u.userId) {
          <button type="button" (click)="startDm(u)">
            <strong>{{ u.displayName }}</strong>
            <span>{{ u.email }}</span>
          </button>
        }
      </div>
      <button mat-button type="button" (click)="showNewDm.set(false)">Cancel</button>
    </div>
  </div>
}

@if (showNewGroup()) {
  <div class="modal-backdrop" (click)="showNewGroup.set(false)">
    <div class="modal" (click)="$event.stopPropagation()">
      <h3>Create group</h3>
      <input type="text" placeholder="Group name" [ngModel]="groupName()" (ngModelChange)="groupName.set($event)" />
      <input type="text" placeholder="Description (optional)" [ngModel]="groupDesc()" (ngModelChange)="groupDesc.set($event)" />
      <input type="search" placeholder="Add members" [ngModel]="dirSearch()" (ngModelChange)="onDirSearch($event)" />
      <div class="dir-list">
        @for (u of directory(); track u.userId) {
          <label class="dir-check">
            <input type="checkbox" [checked]="groupMembers().includes(u.userId)" (change)="toggleGroupMember(u.userId)" />
            <span>{{ u.displayName }} <small>{{ u.email }}</small></span>
          </label>
        }
      </div>
      <div class="modal-actions">
        <button mat-button type="button" (click)="showNewGroup.set(false)">Cancel</button>
        <button mat-flat-button color="primary" type="button" (click)="createGroup()">Create</button>
      </div>
    </div>
  </div>
}

@if (previewImage(); as src) {
  <div class="lightbox" (click)="previewImage.set(null)">
    <img [src]="src" alt="Preview" />
  </div>
}
